import { Component, HostListener, Inject, OnInit, Optional, ViewChild, ElementRef, WritableSignal, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ExpectFundResponseDto } from '../shared/models/expectResponseDto';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { AttachmentData, RequestDocAttachmentResponse } from '../shared/models/requestDocAttachment';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { SnackbarService } from '../shared/services/snackbar.service';
import { BankAccount } from '../shared/models/bankAccount';
import { BankAccountService } from '../shared/services/bank-account.service';
import { Matter } from '../shared/models/matter';
import { ErrorStateMatcher, MatOptionSelectionChange } from '@angular/material/core';
import { MatterService } from '../shared/services/matter.service';
import { CommonService } from '../shared/services/common.service';
import { debounceTime, firstValueFrom, switchMap } from 'rxjs';
import { ApprovalDetails } from '../shared/models/ApprovalDetails';
import { RequestsService } from '../shared/services/requests.service';
import { RequestType, RequestTypeOrder } from '../enums/request-type.enum';
import { UserRequestsService } from '../shared/services/user-requests.service';
import { AuditService } from '../shared/services/audit.service';

@Component({
    selector: 'xp-view-user-expect',
    templateUrl: './view-user-expect.component.html',
    styleUrl: './view-user-expect.component.scss'
})
export class ViewUserExpectComponent implements OnInit {
    ExpectFundForm!: FormGroup;
    selectedPaymentMethod: string = '';
    paymentMethodDropdown: string[] = ['Electronic funds transfer', 'Cheque'];
    matterDropdownData: Matter[] = [];
    isLoading: Boolean = false;
    noData: Boolean = false;
    matterNo: string = '';
    /** The clients on the request's matter. With more than one, "Received from" is ticked from them. */
    clientOptions: string[] = [];
    /** The clients ticked in the dropdown. Only the text is part of the form; it is these names joined. */
    readonly payeeClients = new FormControl<string[]>([], { nonNullable: true });
    readonly payeeErrorMatcher: ErrorStateMatcher = {
        isErrorState: () => {
            const payee = this.ExpectFundForm?.get('payer');
            return !!payee && payee.touched && payee.invalid;
        }
    };
    matterDesc: string = '';
    previousMatterNumber: string | null = null;
    currentDate: Date = new Date(Date.now());
    displayValue: string | null = '';
    paymentType!: boolean;
    filteredBankAccounts: BankAccount[] = [];
    displayFile: string[] = [];
    fileNames: AttachmentData[] = [];
    bankAccounts: BankAccount[] = [];
    bankAccount!: string;
    clientOffice: string = '';
    showReturnReason: boolean = false;
    areMoniesReceived: boolean = false;
    isDisabled = false;
    isRejectDisabled = false;
    isWithdrawDisabled = false;
    showLinkToOpenMatter: WritableSignal<boolean> = signal(false);
    actionstepBaseUrl: string = '';
    actionstepOverviewBaseUrl: string = '';
    actionstepMatterUrl: string = '';
    actionstepMatterOverviewUrl: string = '';
    selectedBankAccountId: string = '';
    showDateTransaction: WritableSignal<boolean> = signal(false);

    constructor(
        private fb: FormBuilder,
        public dialogRef: MatDialogRef<ViewUserExpectComponent>,
        private matterService: MatterService,
        private commonService: CommonService,
        private bankAccountService: BankAccountService,
        private requestService: RequestsService,
        private snackbarService: SnackbarService,
        private userRequestService: UserRequestsService,
        private auditService: AuditService,
        private dialog: MatDialog,
        @Inject(MAT_DIALOG_DATA) public receipt: ExpectFundResponseDto,)
    {}
    async ngOnInit() {
        this.initializeForm();
        this.setupAutocomplete();
        this.bankAccounts = await this.bankAccountService.getAllBankAccounts();

        this.actionstepBaseUrl = await this.requestService.getActionstepUrl();
        this.actionstepOverviewBaseUrl = await this.requestService.getActionStepOverviewUrl();

        this.paymentType = (this.receipt.clientOffice?.toLowerCase() === "office");
        this.clientOffice = this.receipt.clientOffice ?? '';
        await this.filterBankAccounts();

        this.ExpectFundForm.get('clientBankAccount')!.valueChanges.subscribe((account: BankAccount | null) => {
            this.selectedBankAccountId = account?.accountId ?? '';
            if (this.matterNo) {
                this.actionstepMatterUrl = `${this.actionstepBaseUrl}?action_id=${this.matterNo}&ac_account_id=${this.selectedBankAccountId}`;
            }
        });

        if (this.receipt) {
            if (this.receipt.matterNo) {
                const matterData = await this.matterService.getMatterList(this.receipt.matterNo.toString(), '');
                this.matterDropdownData = matterData || [];         }
            this.patchFormValues(this.receipt);
            this.toggleFormFieldsByStage();
            if (this.matterNo) {
                await this.getClientDetails(Number(this.matterNo), false);
            }
        }

        this.bankAccounts = await this.bankAccountService.getAllBankAccounts();

        this.filteredBankAccounts = this.bankAccounts;

        if (this.receipt.clientBankAccountId) {
            this.ExpectFundForm.patchValue({
                clientBankAccount: this.resolveBankAccount(this.receipt.clientBankAccountId) ?? null
            });
        }

        this.ExpectFundForm.get('moniesCheckbox')!.valueChanges.subscribe(async (isMoniesReceived: boolean) => {
            if (isMoniesReceived) {
                if (!this.matterNo) {
                    const matterNumber = this.ExpectFundForm.get('matterNumber')?.value;
                    if (!matterNumber) {
                        this.snackbarService.errorSnackBar("Please select a matter first.");
                        this.ExpectFundForm.get('moniesCheckbox')?.setValue(false, { emitEvent: false });
                        return;
                    }
                    this.matterNo = matterNumber;
                }
                this.isLoading = true;
                await this.getClientDetails(Number(this.matterNo));
                this.isLoading = false;
            } else {
                this.ExpectFundForm.get('payer')?.reset();
                this.payeeClients.setValue([]);
            }
        });


    }

    initializeForm() {
        this.ExpectFundForm = this.fb.group({
            returnReason: [''],
            matter: [null, Validators.required],
            matterNumber: [null, Validators.required],
            clientName: ['', Validators.required],
            reason: ['', Validators.required],
            expValPayment: ['', Validators.required],
            name: '',
            notesToAccounts: '',
            partnerAuthorising: '',
            uploadedFiles: [[]],
            paymentMethod: ['', Validators.required],
            receiptType: [''],
            transactionReference: [''],
            checkDate: [null],
            checkFrom: [''],
            checkNumber: [''],
            checkMemo: [''],
            checkBankName: [''],
            checkBankBranch: [''],
            lastFourNumbers: [''],
            authorizationNumber: [''],
            clientBankAccount: ['', Validators.required],
            clientOffice: ['', Validators.required],
            confirm: [false],
            dateRequested: [null, Validators.required],
            requestedTime: [{ value: '' }],
            isPartnerCertified: false,
            moniesCheckbox: [false, Validators.required],
            payer: ['', Validators.required],
            dateTransaction: '',
            transactionTime:''
        })
        this.ExpectFundForm.get('paymentMethod')?.valueChanges.subscribe(paymentMethod => {
            this.setValidationForPaymentMethod(paymentMethod);
        });
    }

    patchFormValues(expectFund: ExpectFundResponseDto) {
        this.ExpectFundForm.patchValue({
            returnReason: expectFund.returnReason,
            matter: expectFund.matter ? `${expectFund.matterNo} - ${expectFund.matter}` : null,
            matterNumber: expectFund.matterNo || null,
            clientName: expectFund.clientName || '',
            reason: expectFund.paymentReason,
            expValPayment: expectFund.expectedAmount,
            name: expectFund.payerName,
            notesToAccounts: expectFund.notesToAccounts,
            partnerAuthorising: expectFund.partnerAuthorisingName ?? '',
            paymentMethod: expectFund.paymentMethod,
            receiptType: expectFund.receiptType ?? '',
            transactionReference: expectFund.transactionReference,
            checkDate: expectFund.checkDate,
            checkFrom: expectFund.checkFrom,
            checkNumber: expectFund.checkNumber,
            checkMemo: expectFund.checkMemo,
            checkBankName: expectFund.checkBankName,
            checkBankBranch: expectFund.checkBankBranch,
            lastFourNumbers: expectFund.lastFourNumbers,
            authorizationNumber: expectFund.authorizationNumber,
            clientOffice: expectFund.clientOffice,
            dateRequested: expectFund.dateRequested ? new Date(expectFund.dateRequested) : null,
            moniesCheckbox: expectFund.moniesReturnedClient ?? false,
            payer: expectFund.payerName || '',
            dateTransaction: expectFund.dateTransaction
        });

        if (expectFund.dateTransaction) {
            const d = new Date(expectFund.dateTransaction);
            this.ExpectFundForm.get('transactionTime')?.setValue(this.toTimeString(d));
            this.showDateTransaction.set(true);
        }
        else {
            this.showDateTransaction.set(false);
        }

        if (expectFund.dateRequested) {
            const d = new Date(expectFund.dateRequested);
            this.ExpectFundForm.get('requestedTime')?.setValue(this.toTimeString(d));
        }

        this.selectedPaymentMethod = expectFund.paymentMethod ?? '';

        this.matterNo = expectFund?.matterNo?.toString() || '';

        if (this.matterNo) {
            this.actionstepMatterUrl = `${this.actionstepBaseUrl}?action_id=${this.matterNo}&ac_account_id=${this.selectedBankAccountId}`;
            this.actionstepMatterOverviewUrl = `${this.actionstepOverviewBaseUrl}/${this.matterNo}`;

            this.showLinkToOpenMatter.set(true);
        }

    }

    private setupAutocomplete() {
        this.ExpectFundForm.controls['matter'].valueChanges.pipe(
            debounceTime(300),
            switchMap((query) => {
                let id: string = '';
                let name: string = '';

                if (!isNaN(Number(query))) {
                    id = query;
                    name = '';
                }
                else {
                    id = '';
                    name = query;
                }
                if (id == null) {
                    id = ''
                }
                this.isLoading = true;
                this.noData = true;
                return this.matterService.getMatterList(id, name);
            })
        ).subscribe({
            next: (data) => {
                this.noData = false;
                if (data == null) {
                    this.noData = true;
                }
                this.matterDropdownData = data;
                this.isLoading = false;
            },
            error: (error) => {
                this.noData = true;
                if (error.status == 401) {
                    this.commonService.apiSecurityError();
                }
            },
            complete: () => {
                this.isLoading = false;
            }
        })
    }

    private resolveBankAccount(accountId?: string): BankAccount | undefined {
        return this.bankAccounts.find(a => a.accountId === accountId);
    }

    private toTimeString(date: Date): string {
        const pad = (n: number) => String(n).padStart(2, '0');
        return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
    }

    onPaymentMethodChange(): void {
        this.selectedPaymentMethod = this.ExpectFundForm.value.paymentMethod;
    }

    async clearSearchFilter() {
        this.ExpectFundForm.controls.matter.reset();
        this.ExpectFundForm.controls.matterNumber.reset();
        this.matterDropdownData = [];
    }

    displayMatter(matter: Matter) {
        return matter ? `${matter.number} - ${matter.description}` : '';
    }

    async lookupSelecttionChangd(event: MatOptionSelectionChange<string>) {
        if (!event.isUserInput) return;

        const res = event.source.viewValue;
        if (!res || !res.includes(' - ')) return;

        this.matterNo = res.split(' - ')[0];
        this.matterDesc = res.split(' - ')[1];

        if (this.previousMatterNumber === this.matterNo) return;
        this.previousMatterNumber = this.matterNo;

        this.ExpectFundForm.patchValue({
            matter: `${this.matterNo} - ${this.matterDesc}`,
            matterNumber: this.matterNo
        });

        if (this.matterNo) {
            this.actionstepMatterUrl = `${this.actionstepBaseUrl}?action_id=${this.matterNo}&ac_account_id=${this.selectedBankAccountId}`;
            this.actionstepMatterOverviewUrl = `${this.actionstepOverviewBaseUrl}/${this.matterNo}`;
            this.showLinkToOpenMatter.set(true);
        }

        this.ExpectFundForm.get('clientName')?.setValue('');

        await this.getClientDetails(Number(this.matterNo));
    }

    /**
     * Loads the matter's clients into the Client Name field and as the options "Received from" is ticked from.
     * @param resetPayer True when the matter has changed or "monies coming from client" was just set to Yes: the Client
     * Name is refreshed and a payer that follows the client is chosen again. False when the request is first shown,
     * so the Client Name and payer saved with the request are kept.
     */
    async getClientDetails(matterNumber: number, resetPayer = true) {
        try {
            this.clientOptions = await this.matterService.getMatterClientNames(matterNumber);
        } catch {
            this.clientOptions = [];
        }
        if (resetPayer) {
            this.ExpectFundForm.get('clientName')?.setValue(
                this.clientOptions.length ? this.clientOptions.join(', ') : 'No client found');
        }

        if (resetPayer && this.ExpectFundForm.get('moniesCheckbox')?.value === true) {
            // With several clients the user ticks which of them the funds come from, so it starts empty.
            this.ExpectFundForm.get('payer')?.setValue(
                this.clientOptions.length > 1 ? '' : (this.clientOptions[0] ?? 'No client found'));
        }
        this.syncPayeeClients();
    }

    /** True when monies come from the client, the matter has several clients and the payer text can be shown as ticks. */
    get showPayeeSelect(): boolean {
        return this.clientOptions.length > 1
            && this.ExpectFundForm.get('moniesCheckbox')?.value === true
            && this.payeeMatchesClients();
    }

    /**
     * True when the text in the field is empty or is exactly the ticked clients. Text saved before names were shown
     * as "First Last" (or typed by hand) is not, so it is shown as plain text rather than as a blank dropdown.
     */
    private payeeMatchesClients(): boolean {
        const text = this.ExpectFundForm.get('payer')?.value ?? '';
        const ticked = this.payeeClients.value;
        return !text || text === this.clientOptions.filter(name => ticked.includes(name)).join(', ');
    }

    /** Sets the text to the ticked clients, in the order they are listed. */
    onPayeeClientsChange(): void {
        const ticked = this.payeeClients.value;
        this.ExpectFundForm.get('payer')?.setValue(this.clientOptions.filter(name => ticked.includes(name)).join(', '));
    }

    onPayeeSelectOpened(isOpen: boolean): void {
        if (!isOpen) this.ExpectFundForm.get('payer')?.markAsTouched();
    }

    /** Ticks the listed clients that appear in the current text, and locks the dropdown when the field is locked. */
    private syncPayeeClients(): void {
        const payeeControl = this.ExpectFundForm.get('payer');
        const padded = `, ${payeeControl?.value ?? ''}, `;
        this.payeeClients.setValue(this.clientOptions.filter(name => padded.includes(`, ${name}, `)));
        if (payeeControl?.disabled) {
            this.payeeClients.disable();
        } else {
            this.payeeClients.enable();
        }
    }


    validateInput(event: KeyboardEvent): void {
        const allowedKeys = /[0-9.,]/;
        const key = event.key;

        if (['Backspace', 'ArrowLeft', 'ArrowRight', 'Tab', 'Delete'].includes(key)) {
            return;
        }

        if (!allowedKeys.test(key)) {
            event.preventDefault();
        }
    }

    private setValidationForPaymentMethod(paymentMethod: string) {
        const checkFields = ['checkFrom', 'checkNumber', 'checkBankName', 'checkDate'];
        const creditCardFields = ['lastFourNumbers', 'authorizationNumber'];

        this.resetValidation();

        if (!this.isPaymentMethodFieldsVisible) {
            return;
        }

        const fieldsToRequire = paymentMethod === 'Cheque' ? checkFields : paymentMethod === 'Credit Card' ? creditCardFields : [];

        fieldsToRequire.forEach(field => {
            const control = this.ExpectFundForm.get(field);
            control?.setValidators([Validators.required]);
            control?.updateValueAndValidity();
        });
    }

    private resetValidation() {
        const allFields = [
            'checkDate', 'checkFrom', 'checkNumber', 'checkBankName',
            'lastFourNumbers', 'authorizationNumber',
        ];

        allFields.forEach(field => {
            this.ExpectFundForm.get(field)?.clearValidators();
            this.ExpectFundForm.get(field)?.updateValueAndValidity();
        });
    }
    get isPaymentMethodFieldsVisible(): boolean {
        const stageId = this.receipt?.currentStage?.stageOrder;
        return (stageId ?? 0) >= 5;
    }

    formatNumber() {
        if (this.ExpectFundForm.get('expValPayment')?.value == 0) {
            this.displayValue = ""
            this.ExpectFundForm.get('expValPayment')?.setValue(this.displayValue);
        } else {
            this.displayValue = this.commonService.formatNumberWithoutRounding(this.ExpectFundForm.get('expValPayment')?.value)
            this.ExpectFundForm.get('expValPayment')?.setValue(this.displayValue);
        }
    }

    filterBankAccounts() {
        this.filteredBankAccounts = this.bankAccounts;
    }

    onFilesSelected(input: any) {
        if (input.files) {
            this.fileNames = [];
            this.displayFile = [];
            const filesArray = Array.from(input.files);

            filesArray.forEach((file: any) => {
                const fileName = file.name;
                this.displayFile.push(fileName);
                const reader = new FileReader();
                reader.onload = (e: any) => {
                    const bytes = e.target.result.split('base64,')[1];
                    this.fileNames.push({ fileName: fileName, attachments: bytes });
                }
                reader.readAsDataURL(file);
            });
        }
        this.ExpectFundForm.get('uploadedFiles')?.setValue(this.fileNames);
    }

    private toggleFormFieldsByStage() {
        const isParalegalStage = this.receipt.currentStage?.stageOrder === 2;
        const isPartnerStage = (this.receipt.currentStage?.stageOrder === 3 && this.receipt.isCreatorPartnerApprove) ?? false;

        this.isRejectDisabled = this.receipt.currentStage?.stageOrder === 1 || (this.receipt.currentStage?.stageOrder ?? 0) >= 5;
        this.isWithdrawDisabled = this.receipt.currentStage?.stageOrder === 1 || this.receipt.currentStage?.stageOrder === 7;

        const isEditable = isParalegalStage || isPartnerStage;
        this.isDisabled = !(isParalegalStage || isPartnerStage);

        Object.keys(this.ExpectFundForm.controls).forEach(controlName => {
            const control = this.ExpectFundForm.get(controlName);
            if (isEditable) {
                control?.enable();
            } else {
                control?.disable();
            }
        });

        this.ExpectFundForm.get('dateRequested')?.disable();
        this.ExpectFundForm.get('requestedTime')?.disable();

        this.showReturnReason = isEditable;
    }
    closeDialog() {
        this.dialogRef.close(true);
    }

    onUrlCopied() {
        this.snackbarService.successSnackBar("URL copied to clipboard");
    }

    clearUpload(fileInput: any) {
        this.displayFile = [];
        this.fileNames = [];
        this.ExpectFundForm.get('uploadedFiles')?.setValue([]);
        fileInput.value = '';
    }

    uploadDoc() {
        if (this.ExpectFundForm.get('uploadedFiles')?.value.length > 0) {
            let docData: RequestDocAttachmentResponse = {
                requestId: this.receipt.id,
                requestType: RequestType.ExpectFunds,
                attachments: this.ExpectFundForm.get('uploadedFiles')?.value
            }

            this.requestService.addRequestDocument(docData).then((res) => {
                this.snackbarService.successSnackBar(`${res}`);
            })
        }
        this.dialogRef.close(true);
    }

    delete() {

        var configOk = {
            title: 'Are you sure?',
            message: 'The data associated with the request will be deleted.',
            dialogType: 'warning',
            buttonType: 'ConfirmCancel',
            confirmText: 'Confirm',
            cancelText: 'Cancel',
        }
        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            data: configOk,
            width: '30%',
        });

        dialogRef.afterClosed().subscribe(res => {
            if (res) {
                this.requestService.deleteExpectFund(this.receipt.id)
                    .then((res) => {
                        if (res) {
                            this.snackbarService.successSnackBar(`${res}`);
                            this.dialogRef.close(true);
                        }
                    })
                    .catch((error) => {
                        this.snackbarService.errorSnackBar("Failed to delete expected fund request. Please try again.");
                    });
            }
        })
    }

    async approveRequest() {
        if (this.ExpectFundForm.invalid) {
            this.ExpectFundForm.markAllAsTouched();
            return;
        }
        const isPartnerStage = (this.receipt.currentStage?.stageOrder === 3 && this.receipt.isCreatorPartnerApprove) ?? false;
        const selectedAccount = this.resolveBankAccount(this.selectedBankAccountId);

        const approvalDetails: ApprovalDetails = {
            reasonForReceipt: this.ExpectFundForm.value.reason,
            amount: this.commonService.parseFormattedNumber(this.ExpectFundForm.value.expValPayment),
            paymentMethod: this.ExpectFundForm.value.paymentMethod,
            clientBankAccountId: this.selectedBankAccountId || this.receipt.clientBankAccountId,
            clientBankAccountName: selectedAccount
                ? `${selectedAccount.accountId} - ${selectedAccount.description}`
                : this.receipt.clientBankAccountName,
            payee: this.ExpectFundForm.value.payer,
            clientOffice: this.ExpectFundForm.value.clientOffice,
            requestDate: this.ExpectFundForm.value.dateRequested,
            notesToAccounts: this.ExpectFundForm.value.notesToAccounts,

            transactionReference: this.ExpectFundForm.value.transactionReference,

            checkDate: this.ExpectFundForm.value.checkDate,
            checkFrom: this.ExpectFundForm.value.checkFrom,
            checkNumber: this.ExpectFundForm.value.checkNumber,
            checkMemo: this.ExpectFundForm.value.checkMemo,
            checkBankName: this.ExpectFundForm.value.checkBankName,
            checkBankBranch: this.ExpectFundForm.value.checkBankBranch,

            lastFourNumbers: this.ExpectFundForm.value.lastFourNumbers,
            authorizationNumber: this.ExpectFundForm.value.authorizationNumber,

            uploadedFiles: this.ExpectFundForm.value.uploadedFiles,
            moniesReturnedToClient: this.ExpectFundForm.value.moniesCheckbox,
            confirm: this.ExpectFundForm.value.confirm,
            clientName: this.ExpectFundForm.value.clientName,
            matter: this.matterDesc || this.receipt.matter,
            matterNumber: this.ExpectFundForm.value.matterNumber?.toString(),
        };

        if (isPartnerStage) {
            approvalDetails.isPartnerCertified = true;
        }

        this.requestService.approveRequest(
            this.receipt.id,
            RequestType.ExpectFunds,
            (this.receipt.currentStage!.id) + 1,
            approvalDetails
        )
            .then((res: string) => {
                if (res) {
                    this.snackbarService.successSnackBar(`${res}`);
                    this.dialogRef.close(true);
                    this.uploadDoc();
                }
            })
            .catch((error) => {
                this.snackbarService.errorSnackBar("Failed to approve receipt request. Please try again.");
            });
    }

    async withdrawReceipt() {
        const audits = await this.auditService.getUserAuditByRequest(this.receipt.id, RequestTypeOrder[RequestType.ExpectFunds]);

        const latestAudit = audits?.length ? audits[audits.length - 1] : null;
        const rpaStatus = latestAudit?.wasRpaSuccessful ?? null;
        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            width: '600px',
            data: {
                title: 'Withdraw Expect Fund Request?',
                message: 'Are you sure you want to withdraw this expect fund request?',
                dialogType: 'warning',
                buttonType: 'ConfirmCancel',
                confirmText: 'Confirm',
                cancelText: 'Cancel',
                matterNo: this.receipt?.matterNo,
                currentStageId: this.receipt?.currentStage?.id,
                rpaStatus: rpaStatus,
                skipAccountsValidation: true,
                selectedBankAccountId: this.selectedBankAccountId,
            }
        });
        dialogRef.afterClosed().subscribe(res => {
            if (res) {
                this.userRequestService.withdrawExpectFund(this.receipt.id)
                    .then((res) => {
                        if (res) {
                            this.snackbarService.successSnackBar(`${res}`);
                            this.dialogRef.close(true);
                        }
                    })
                    .catch((error) => {
                        this.snackbarService.errorSnackBar("Failed to withdraw payment request. Please try again.");
                    });
            }
        })
    }

}
